import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

import QuestionnaireStep1 from "../components/QuestionnaireStep1";
import ConsentStep3 from "../components/ConsentStep3";
import PaymentStep from "../components/PaymentStep";

import CareerReportRenderer from "../components/CareerReportRenderer";

import { useCareerFlow } from "../hooks/useCareerFlow";

export default function QuestionnairePage() {
  const navigate =
    useNavigate();

  const {
  STEPS,

  REPORT_PAYMENT_MODE,

  step,
  setStep,

  consentOk,
  handleConsentAccepted,

  report,
  progressText,
  progressPercent,

  paymentPlan,

  // =========================================================
  // ONE-TIME FREE REPORT
  // =========================================================

  freeReportStatus,
  handleViewExistingReport,

  // =========================================================
  // TEMPORARY FLOW
  // =========================================================

  handleViewReport,
  handlePayItForward,

  // =========================================================
  // ORIGINAL STANDARD FLOW
  // =========================================================

  handleStartPayment,
} = useCareerFlow();
  const paymentMode =
    (
      REPORT_PAYMENT_MODE ||
      "temporary"
    )
      .toString()
      .trim()
      .toLowerCase();


  // ===========================================================
  // DEBUG
  //
  // Keep these temporarily while testing.
  // ===========================================================

  console.log(
    "[QuestionnairePage] PAYMENT MODE:",
    paymentMode
  );

  console.log(
    "[QuestionnairePage] ACCOUNT PLAN:",
    paymentPlan
  );


  // ===========================================================
  // STEPPER
  // ===========================================================

  const stepItems = [
    {
      label:
        "Questionnaire",

      step:
        STEPS.QUESTIONNAIRE,
    },

    {
      label:
        "Consent",

      step:
        STEPS.CONSENT,
    },

    {
      label:
        "Report Options",

      step:
        STEPS.PAYMENT,
    },

    {
      label:
        "Results",

      step:
        STEPS.RESULTS,
    },
  ];


  // ===========================================================
  // ORIGINAL STANDARD FLOW
  //
  // IMPORTANT:
  //
  // This is kept for future re-enabling.
  //
  // Snapshot account:
  //
  // US$9.99
  //
  // Deep Audit account:
  //
  // US$19.99
  //
  // ===========================================================

  const handleStandardReportOption =
    async (plan) => {
      const selectedPlan =
        (plan || "")
          .toString()
          .trim()
          .toLowerCase();

      console.log(
        "[QuestionnairePage] STANDARD report selected:",
        selectedPlan
      );

      // -------------------------------------------------------
      // SNAPSHOT
      // -------------------------------------------------------

      if (
        selectedPlan ===
        "snapshot"
      ) {
        await handleStartPayment(
          "snapshot"
        );

        return;
      }

      // -------------------------------------------------------
      // DEEP AUDIT
      // -------------------------------------------------------

      if (
        selectedPlan ===
        "deep_audit"
      ) {
        await handleStartPayment(
          "deep_audit"
        );

        return;
      }

      console.error(
        "[QuestionnairePage] Invalid standard report plan:",
        selectedPlan
      );
    };


  // ===========================================================
  // TEMPORARY FLOW:
  // PAY IT FORWARD
  //
  // IMPORTANT:
  //
  // User DOES NOT choose the report tier.
  //
  // Snapshot account
  // -> Snapshot report
  //
  // Deep Audit account
  // -> Deep Audit report
  //
  // The $5 payment is only an optional contribution.
  // ===========================================================

  const handleTemporaryPayItForward =
    async () => {
      console.log(
        "[QuestionnairePage] PAY IT FORWARD",
        {
          paymentMode,
          paymentPlan,
        }
      );

      await handlePayItForward();
    };


  // ===========================================================
  // TEMPORARY FLOW:
  // VIEW MY REPORT
  //
  // IMPORTANT:
  //
  // NO PAYMENT.
  //
  // Account report tier stays unchanged.
  //
  // snapshot -> Snapshot report free
  // deep_audit -> Deep Audit report free
  // ===========================================================

  const handleTemporaryViewReport =
    async () => {
      console.log(
        "[QuestionnairePage] VIEW MY REPORT",
        {
          paymentMode,
          paymentPlan,
        }
      );

      await handleViewReport();
    };


  return (
    <div className="min-h-screen bg-[#11111f] text-white font-audiowide">

      {/* =====================================================
          TOP BAR
      ===================================================== */}

      <div
        className="
          sticky
          top-0
          z-50
          bg-[#11111f]/90
          backdrop-blur
          border-b
          border-[#EB6AB5]/20
          px-4
          py-4
        "
      >
        <div
          className="
            max-w-6xl
            mx-auto
            flex
            items-center
            justify-between
            gap-4
          "
        >
          <div>

            <p
              className="
                text-[#EB6AB5]
                font-semibold
                text-sm
              "
            >
              QUESTIONNAIRE
            </p>

            <p
              className="
                text-white/80
                text-xs
              "
            >
              Complete all steps to generate your report
            </p>

          </div>


          <button
            type="button"

            onClick={() =>
              navigate(
                "/dashboard"
              )
            }

            className="
              px-4
              py-2
              rounded-full
              bg-[#2b213e]
              border
              border-[#EB6AB5]/40
              text-[#EB6AB5]
              hover:bg-[#3a2b52]
              transition
              text-sm
            "
          >
            ← Back to Dashboard
          </button>

        </div>
      </div>


      <main
        className="
          max-w-6xl
          mx-auto
          px-4
          py-8
        "
      >

        {/* =====================================================
            STEPPER
        ===================================================== */}

        <div
          className="
            rounded-2xl
            border
            border-[#EB6AB5]/20
            bg-[#0f0f1b]/30
            backdrop-blur
            p-4
            sm:p-6
          "
        >

          <div
            className="
              flex
              items-center
              justify-between
              gap-2
            "
          >

            {stepItems.map(
              (
                item,
                idx
              ) => {
                const active =
                  step ===
                  item.step;

                const done =
                  step >
                  item.step;

                return (
                  <div
                    key={
                      item.label
                    }

                    className="
                      flex-1
                      flex
                      flex-col
                      items-center
                    "
                  >

                    <div
                      className={`
                        w-10
                        h-10
                        rounded-full
                        flex
                        items-center
                        justify-center
                        text-sm
                        font-bold
                        transition

                        ${
                          done
                            ? "bg-green-500 text-white"

                            : active
                            ? "bg-[#EB6AB5] text-[#0F0A24] scale-110"

                            : "bg-gray-700 text-gray-200"
                        }
                      `}
                    >

                      {done ? (
                        <CheckCircle2
                          size={16}
                        />
                      ) : (
                        idx + 1
                      )}

                    </div>


                    <span
                      className={`
                        mt-2
                        text-xs

                        ${
                          active
                            ? "text-[#EB6AB5]"
                            : "text-gray-400"
                        }
                      `}
                    >
                      {item.label}
                    </span>

                  </div>
                );
              }
            )}

          </div>


          <div
            className="
              mt-4
              h-1
              w-full
              bg-white/10
              rounded-full
              overflow-hidden
            "
          >

            <div
              className="
                h-1
                bg-gradient-to-r
                from-[#EB6AB5]
                to-[#ff83d1]
                transition-all
              "

              style={{
                width:
                  `${
                    (
                      step /
                      STEPS.RESULTS
                    ) *
                    100
                  }%`,
              }}
            />

          </div>

        </div>


        {/* =====================================================
            CONTENT
        ===================================================== */}

        <div
          className="
            mt-6
            rounded-2xl
            border
            border-[#EB6AB5]/20
            bg-[#0f0f1b]/30
            backdrop-blur
            p-4
            sm:p-8
          "
        >

          <motion.div
            key={step}

            initial={{
              opacity: 0,
              y: 18,
            }}

            animate={{
              opacity: 1,
              y: 0,
            }}

            transition={{
              duration: 0.35,
            }}
          >

            {/* =================================================
                QUESTIONNAIRE
            ================================================= */}

            {step ===
              STEPS.QUESTIONNAIRE && (

              <QuestionnaireStep1
                onComplete={() =>
                  setStep(
                    STEPS.CONSENT
                  )
                }
              />

            )}


            {/* =================================================
                CONSENT
            ================================================= */}

            {step ===
              STEPS.CONSENT && (

              <ConsentStep3

                prevStep={() =>
                  setStep(
                    STEPS.QUESTIONNAIRE
                  )
                }

                onAccept={
                  handleConsentAccepted
                }

              />

            )}


            {/* =================================================
                REPORT OPTIONS
            ================================================= */}

            {step ===
              STEPS.PAYMENT && (

              <PaymentStep

  // =============================================
  // ACTUAL USER REPORT PLAN
  //
  // snapshot
  // deep_audit
  //
  // This is NOT payment choice.
  // =============================================

  plan={
    paymentPlan
  }


  // =============================================
  // FEATURE FLAG
  //
  // temporary / standard
  // =============================================

  paymentMode={
    paymentMode
  }


  // =============================================
  // ONE-TIME FREE REPORT STATUS
  // =============================================

  freeReportStatus={
    freeReportStatus
  }


  // =============================================
  // BACK
  // =============================================

  onBack={() =>
    setStep(
      STEPS.CONSENT
    )
  }


  // =============================================
  // TEMPORARY FLOW
  //
  // Pay $5 voluntarily.
  // Same account report.
  // =============================================

  onPayItForward={
    handleTemporaryPayItForward
  }


  // =============================================
  // TEMPORARY FLOW
  //
  // First-time free report.
  // =============================================

  onViewReport={
    handleTemporaryViewReport
  }


  // =============================================
  // EXISTING FREE REPORT
  //
  // NO GEMINI CALL
  // =============================================

  onViewExistingReport={
    handleViewExistingReport
  }


  // =============================================
  // ORIGINAL STANDARD FLOW
  //
  // $9.99 / $19.99
  // =============================================

  onContinue={
    handleStandardReportOption
  }


  consentOk={
    consentOk
  }
/>
            )}


            {/* =================================================
                RESULTS
            ================================================= */}
{step === STEPS.RESULTS && (
  <div className="space-y-8">

    {!report?.trim() ? (

      // =============================================
      // GENERATING REPORT
      // KEEP THIS CENTERED
      // =============================================

      <div
        className="
          flex
          flex-col
          items-center
          gap-6
          text-center
        "
      >
        <div
          className="
            w-20
            h-20
            border-4
            border-[#EB6AB5]/30
            border-t-[#EB6AB5]
            rounded-full
            animate-spin
          "
        />

        <p
          className="
            text-[#EB6AB5]
            text-lg
            font-semibold
          "
        >
          {progressText || "Initializing AI analysis..."}
        </p>

        <div
          className="
            w-full
            max-w-sm
            bg-[#2b213e]
            rounded-full
            h-3
          "
        >
          <div
            className="
              h-3
              bg-gradient-to-r
              from-[#EB6AB5]
              to-[#ff83d1]
              transition-all
              duration-500
            "
            style={{
              width: `${progressPercent}%`,
            }}
          />
        </div>

        <p
  className="
    text-sm
    text-gray-400
  "
>
  {progressText
    ?.toLowerCase()
    .includes("existing")
      ? "Loading your saved career report…"
      : "Generating your personalized career report…"}
</p>
      </div>

    ) : (

      // =============================================
      // REPORT READY
      // LEFT ALIGNED
      // =============================================

      <div
        className="
          text-white/85
          text-left
          max-w-5xl
          mx-auto
        "
      >
        <h2
          className="
            text-2xl
            font-bold
            text-[#EB6AB5]
            mb-6
            text-left
          "
        >
          🎉 Your Results Are Ready
        </h2>
<CareerReportRenderer
  report={report}
/>
      </div>

    )}

  </div>
)}
          </motion.div>

        </div>

      </main>

    </div>
  );
}